import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import CustomMUITable from "src/components/CustomMUITable";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import Label from "src/components/Label";
import {
  deleteAutomationViaApi,
  loadAutomationsFromApi,
} from "./automationStorage";
import AiProviderSettingsDrawer from "../automationBuilder/AiProviderSettingsDrawer";

const PRIMARY = "var(--portal-theme-primary)";
const MUTED = "#64748b";
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

export default function TrackerAutomationsList() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [aiSettingsOpen, setAiSettingsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await loadAutomationsFromApi();
        if (cancelled) return;
        setAutomations(res.automations || []);
        if (!res.ok && res.message) {
          enqueueSnackbar(res.message, { variant: "error" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  const loadAutomations = async () => {
    setLoading(true);
    try {
      const res = await loadAutomationsFromApi();
      setAutomations(res.automations || []);
      if (!res.ok && res.message) {
        enqueueSnackbar(res.message, { variant: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const trackerId = searchParams.get("trackerId");
    const create = searchParams.get("create");
    if (!trackerId && !create) return;

    const qs = trackerId
      ? `?trackerId=${encodeURIComponent(trackerId)}`
      : "";
    setSearchParams({}, { replace: true });
    navigate(`/tracker-automation-builder/new${qs}`);
  }, [searchParams, setSearchParams, navigate]);

  const filteredAutomations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return automations;
    return automations.filter((row) => {
      const hay =
        `${row.name || ""} ${row.description || ""} ${row.trackerName || ""} ${(
          row.scopeTags || []
        ).join(" ")} ${
          row.status || ""
        }`.toLowerCase();
      return hay.includes(q);
    });
  }, [automations, search]);

  const handleDelete = async () => {
    if (!deleteDoc?.id) return;
    try {
      await deleteAutomationViaApi(deleteDoc.id);
      setOpenDelete(false);
      setDeleteDoc(null);
      await loadAutomations();
      enqueueSnackbar("Automation deleted", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not delete automation", {
        variant: "error",
      });
    }
  };

  const openAutomation = (row) => {
    if (!row?.id) return;
    navigate(`/tracker-automation-builder/${row.id}`);
  };

  const MENU_OPTIONS = [
    {
      label: "Open",
      icon: "akar-icons:eye",
      handleClick: (row) => openAutomation(row),
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: (row) => {
        setDeleteDoc(row);
        setOpenDelete(true);
      },
    },
  ];

  const TABLE_HEAD = [
    {
      id: "action",
      label: "Action",
      type: "action",
      MENU_OPTIONS: "MENU_OPTIONS",
    },
    { id: "number", label: "#", type: "number" },
    {
      id: "name",
      label: "Automation",
      renderData: (row) => (
        <Stack spacing={0.25}>
          <Typography
            variant="subtitle2"
            sx={{
              cursor: "pointer",
              fontWeight: 700,
              color: PRIMARY,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => openAutomation(row)}
          >
            {row.name || "Untitled"}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {row.scopeType === "tags"
              ? `Tags: ${(row.scopeTags || []).join(", ") || "—"}`
              : row.scopeType === "individual"
              ? row.scopeMember?.audienceType === "all"
                ? row.scopeMember?.coachId
                  ? "All my members"
                  : "All members"
                : row.scopeMember?.audienceType === "tracker"
                ? `Journey · ${row.trackerName || "—"}`
                : row.scopeMember?.name || "Specific members"
              : row.trackerName || "—"}
          </Typography>
        </Stack>
      ),
    },
    {
      id: "workflows",
      label: "Workflows",
      sortValue: (row) => (row.workflows || []).length,
      renderData: (row) => String((row.workflows || []).length),
    },
    {
      id: "enabled",
      label: "Enabled",
      renderData: (row) => (
        <Label
          variant="ghost"
          color={row.enabled !== false ? "success" : "default"}
        >
          {row.enabled !== false ? "On" : "Off"}
        </Label>
      ),
    },
    {
      id: "updatedAt",
      label: "Updated",
      renderData: (row) =>
        row.updatedAt ? moment(row.updatedAt).format("DD/MM/YYYY hh:mm A") : "—",
    },
  ];

  const handleCreate = () => {
    navigate("/tracker-automation-builder/new");
  };

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Delete this automation and all its workflows?"
        handleAgree={handleDelete}
      />

      {/* AI settings hidden for now — server env keys; re-enable when needed
      <AiProviderSettingsDrawer
        open={aiSettingsOpen}
        onClose={() => setAiSettingsOpen(false)}
      />
      */}

      <div className="container-fluid tracker-module-page">
        <div className="row">
          <div className="col-lg-8 col-sm-12">
            <h2>Automations</h2>
            <Typography sx={{ color: "text.secondary", mb: 1, fontSize: 14 }}>
              Create as many automations as you need per tracker. Workflows
              hold When / Who / Then / Write and dynamic fields.
            </Typography>
          </div>
          <div className="col-lg-4 col-sm-12 text-end">
            {/* <button
              type="button"
              className="small-contained-outlined-button"
              onClick={() => setAiSettingsOpen(true)}
              style={{ marginRight: 8 }}
            >
              AI settings
            </button> */}
            <button
              type="button"
              className="small-contained-button"
              onClick={handleCreate}
            >
              Create automation
            </button>
          </div>
        </div>

        <Box
          sx={{
            mb: 2.5,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <TextField
            size="small"
            label="Search"
            placeholder="Name or tracker"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ ...fieldSx, width: { xs: "100%", sm: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <CustomMUITable
            TABLE_HEAD={TABLE_HEAD}
            data={filteredAutomations}
            MENU_OPTIONS={MENU_OPTIONS}
            hide_search
            sortable
            onRowClick={(row) => openAutomation(row)}
            className="card-with-background tracker-module-table"
          />
        )}
      </div>
    </>
  );
}
